/**
 * Dashboard page for displaying and managing user assignments, scores,
 * and performance data with filtering, tab navigation, and export features.
 *
 * @module pages/dashboard/Dashboard
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useRef, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import { connect } from 'react-redux';
import { Grid, FormControlLabel, Tooltip } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import InputBase from '@material-ui/core/InputBase';
import SearchIcon from '@material-ui/icons/Search';
import List from '@material-ui/core/List';
import CircularProgress from '@material-ui/core/CircularProgress';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import FileSaver from 'file-saver';
import Badge from '@material-ui/core/Badge';
import DateRangeIcon from '@material-ui/icons/DateRange';
import moment from 'moment';
import AssignmentTable from '../../components/dashboard/AssignmentTable.jsx';
import ScoreFilters from '../../components/dashboard/ScoreFilters.jsx';
import UserData from '../../components/dashboard/UserData.jsx';
import DateFilter from '../../components/dashboard/DateFilter.jsx';
import ScoreTable from '../../components/dashboard/ScoreTable.jsx';
import {
    fetchAssignments,
    fetchUserData,
    fetchTotalAssignments,
    fetchScoreList,
    reset,
    fetchAssignmentsCount,
} from '../../redux/sims/actions';
import Export from '../../assets/images/icons/ExportIcon.png';
import AddAssignment from '../../components/dashboard/AddAssignment.jsx';
import {
    fetchAllAssignments,
    fetchScoreLists,
} from '../../services/result.service';
import DashboardPermissions from '../../permissions/dashboard';
import SimsPermissions from '../../permissions/sims';
import { permissionCheck } from '../../utils/permissionCheck';
import DashboardStyle from './Dashboard';

/**
 * Dashboard component serves as the main interface for displaying and managing
 * user-specific data, assignment details, and performance scores. It integrates
 * various functionalities including data fetching, permission checks, tab navigation,
 * and data filtering to provide a dynamic and interactive dashboard experience.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.userData - User-related data to be displayed on the dashboard.
 * @param {Function} props.fetchUserData - Function to fetch user-specific data.
 * @param {Array} props.assignments - List of assignments available for the user.
 * @param {Function} props.fetchAssignments - Function to fetch assignment details.
 * @param {Function} props.fetchScoreList - Function to fetch the list of user scores.
 * @param {number} props.allAssignmentCount - Total number of all assignments.
 * @param {number} props.myAssignmentCount - Number of assignments specific to the user.
 * @param {number} props.completedAssignmentCount - Count of assignments completed by the user.
 * @param {number} props.readyForReviewCount - Count of assignments ready for review.
 * @param {number} props.unopenedCompletedAssignments - Count of unopened completed assignments.
 * @param {Array} props.scores - Array containing user's scores for assignments.
 * @param {number} props.totalScore - Total score achieved by the user.
 * @param {number} props.pendingForReview - Number of assignments pending for review.
 * @param {boolean} props.assignment_loading - Indicates whether the assignments are currently loading.
 * @param {Object} props.dashboard_listing - Data used for displaying dashboard listings.
 * @param {Object} props.permissions - Object representing user permissions.
 * @param {Object} props.userDetails - Additional details of the logged-in user.
 * @param {Function} props.reset - Function to reset the dashboard's state.
 * @param {Array} props.products - List of products associated with scores and assignments.
 * @param {Function} props.fetchAssignmentsCount - Function to fetch counts for assignments.
 * @param {Object} props.countData - Data object containing various count-related details.
 */
const Dashboard = ({
    userData,
    fetchUserData,
    assignments,
    fetchAssignments,
    fetchScoreList,
    allAssignmentCount,
    myAssignmentCount,
    completedAssignmentCount,
    readyForReviewCount,
    unopenedCompletedAssignments,
    unopenedPendingAssignments,
    scores,
    totalScore,
    pendingForReview,
    assignment_loading,
    dashboard_listing,
    permissions,
    userDetails,
    reset,
    products,
    fetchAssignmentsCount,
    countData,
    simData,
}) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const useStyles = makeStyles((theme) => DashboardStyle(theme));
    const classes = useStyles();
    const [value, setValue] = React.useState('allAssignment');
    const [tabValue, setTabValue] = React.useState('myAssignment');
    const [showAddAssignment, setshowAddAssignment] = useState(false);
    const [inMyAssignmentTab, setInMyAssignmentTab] = useState(false);
    const [inReadyReviewTab, setInReadyReviewTab] = useState(false);
    const [textSearch, setTextSearch] = useState('');
    const [text, setText] = React.useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const [assign, setAssign] = useState([]);
    const [scoreList, setScoreList] = useState([]);
    const [finalResult, setFinalResult] = useState(true);
    const [highestLevel, setHighestLevel] = useState(false);
    const [permission, setPermission] = useState(false);
    const [viewPermission, setViewPermission] = useState('');
    const [userPermission, setUserPermission] = useState(false);
    const [order, setOrder] = React.useState(null);
    const [dateRange, setDateRange] = useState(false);
    const [orderBy, setOrderBy] = React.useState(null);
    const [sort, setSort] = React.useState(null);
    const [sortBy, setSortBy] = React.useState(null);
    const [filterDatas, setFilterDatas] = useState({
        product: '',
        groups: [],
        members: [],
        sims: [],
        assigned: true,
        un_assigned: false,
    });
    const [page, setPage] = useState(1);
    const [sortPage, setSortPage] = useState(1);
    const defaultProduct = useRef('');
    localStorage.setItem('sims_id', '');

    useEffect(() => {
        if (
            permissionCheck(
                DashboardPermissions.user_account.view.task,
                DashboardPermissions.user_account.view.type,
            )
        ) {
            fetchUserData();
        }
    }, [fetchUserData, permissions]);

    if (
        permissionCheck(
            DashboardPermissions.all_assignments.view.task,
            DashboardPermissions.all_assignments.view.type,
        ) !== undefined
        && permissionCheck(
            DashboardPermissions.all_assignments.view.task,
            DashboardPermissions.all_assignments.view.type,
        )
        && !permission
    ) {
        setPermission(true);
    }

    if (
        permissionCheck(
            SimsPermissions.assignment.view.task,
            SimsPermissions.assignment.view.type,
        ) !== undefined
        && !viewPermission && (permissionCheck(
                SimsPermissions.assignment.view.task,
                SimsPermissions.assignment.view.type,
            )
            || permissionCheck(
                DashboardPermissions.review_assignments.view.task,
                DashboardPermissions.review_assignments.view.type,
            )
            || permissionCheck(
                DashboardPermissions.all_assignments.view.task,
                DashboardPermissions.all_assignments.view.type,
            ))
    ) {
        setViewPermission(
            permissionCheck(
                SimsPermissions.assignment.view.task,
                SimsPermissions.assignment.view.type,
            )
            || permissionCheck(
                DashboardPermissions.review_assignments.view.task,
                DashboardPermissions.review_assignments.view.type,
            )
            || permissionCheck(
                DashboardPermissions.all_assignments.view.task,
                DashboardPermissions.all_assignments.view.type,
            ),
        );
    }

    if (
        (permissionCheck(
                DashboardPermissions.company_name.view.task,
                DashboardPermissions.company_name.view.type,
            )
            || permissionCheck(
                DashboardPermissions.user_account.view.task,
                DashboardPermissions.user_account.view.type,
            )
            || permissionCheck(
                DashboardPermissions.user_name.view.task,
                DashboardPermissions.user_name.view.type,
            ))
        && !userPermission
    ) {
        setUserPermission(true);
    }

    useEffect(() => {
        if (viewPermission) {
            if (!permission) {
                // Check if all assignment permission is denied, so check for ready review option
                if (
                    !permissionCheck(
                        DashboardPermissions.all_assignments.view.task,
                        DashboardPermissions.all_assignments.view.type,
                    )
                ) {
                    setValue('myAssignment');
                    const tab = 'MY_ASSIGNMENT';
                    setPage(1);
                    fetchAssignments(pageNumber, textSearch, true, tab, orderBy, order, 0);
                }
            } else {
                setValue('allAssignment');
                setPage(1);
                const tab = 'All_ASSIGNMENT';
                fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, 0);
            }
            fetchAssignmentsCount();
        }
        // eslint-disable-next-line max-len
    }, [fetchAssignments, permission, viewPermission]);

    useEffect(() => {
        if (viewPermission && permission) {
            if (tabValue === 'myAssignment') {
                if (value === 'myAssignment' && permission) {
                    const tab = 'MY_ASSIGNMENT';
                    setPage(1);
                    fetchAssignments(pageNumber, textSearch, true, tab, orderBy, order, 0);
                } else if (value === 'allAssignment') {
                    const tab = 'All_ASSIGNMENT';
                    setPage(1);
                    fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, 0);
                } else if (value === 'completed') {
                    const tab = 'COMPLETED';
                    setPage(1);
                    fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, 0);
                } else if (value === 'readyForReview') {
                    const tab = 'PENDING_REVIEW';
                    setPage(1);
                    fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, 0);
                }
            }
        }
        // eslint-disable-next-line max-len
    }, [pageNumber, textSearch, fetchAssignments, permission, viewPermission]);

    useEffect(() => {
        setAssign(assignments);
    }, [assignments]);

    useEffect(
        () => {
            let filterData;
            if (!filterDatas.product) {
                filterData = {
                    ...filterDatas,
                    product: getDefaultProduct(),
                };
            } else {
                filterData = filterDatas;
            }
            setFilterDatas(filterData);
            setSortPage(1);
            fetchScoreList(pageNumber, textSearch, finalResult, highestLevel, filterData, sortBy, sort, 0);
        },
        [pageNumber, textSearch, finalResult, highestLevel, defaultProduct.current]
    );

    useEffect(() => {
        if (tabValue === 'score' && products.length) {
            setDefaultProduct(getDefaultProduct());
        }
    }, [tabValue, products]);

    useEffect(() => {
        setScoreList(scores);
    }, [scores]);

    const setDefaultProduct = (id) => {
        defaultProduct.current = id;
    }

    const getDefaultProduct = () => {
        let result = defaultProduct.current?.length ?
            defaultProduct.current :
            (
                products?.length ?
                    products.find(prod =>
                        (prod?.name?.toLowerCase().indexOf('training') > -1) &&
                        (prod?.name?.toLowerCase().indexOf('voice') > -1)
                    ):
                    null
            );

        return result?.length ? result : result?._id ?? '';
    }

    useEffect(
        () => {
            //console.log('simData = %o', simData);
        },
        [simData]
    );

    /**
     * Handles changes triggered by a user event and updates the application state accordingly.
     *
     * This function performs various state changes such as resetting pagination,
     * updating tab values, clearing filters, and resetting assignment-related data.
     * It also manages permissions and determines which tab or data to display
     * based on the received event value and current state.
     *
     * @param {Object} event - The event object triggered by user interaction (e.g., a tab change).
     * @param {Object} event.currentTarget - The current target of the event.
     * @param {string} event.currentTarget.value - The value associated with the selected option or tab.
     */
    const handleChange = (event) => {
        setPageNumber(0);
        setTabValue(event.currentTarget.value);
        setFilterDatas({});
        setAssign([]);
        setScoreList([]);
        setFinalResult(true);
        setHighestLevel(false);
        if (
            !permissionCheck(
                DashboardPermissions.all_assignments.view.task,
                DashboardPermissions.all_assignments.view.type,
            )
        ) {
            setValue('myAssignment');
        } else {
            setValue('allAssignment');
        }

        if (event.currentTarget.value === 'myAssignment') {
            if (value === 'allAssignment') {
                const myAssignment = false;
                setInMyAssignmentTab(false);
                setInReadyReviewTab(false);
                setPage(1);
                fetchAssignments(pageNumber, textSearch, myAssignment, null, orderBy, order, 0);
            } else {
                const myAssignment = true;
                setInMyAssignmentTab(true);
                setInReadyReviewTab(false);
                const tab = 'MY_ASSIGNMENT';
                setPage(1);
                fetchAssignments(pageNumber, textSearch, myAssignment, tab, orderBy, order, 0);
            }
        } else {
            setAssign([]);
        }
    };

    /**
     * Asynchronous function to refresh the current state of assignments and their count.
     *
     * This function resets the assignment-related states, page number, and the current page,
     * then fetches a fresh list of assignments and their total count based on the existing
     * search and sort parameters.
     *
     * The function performs the following steps:
     * - Resets the assignments list to an empty array.
     * - Resets the page number to 0 and the current page to 1.
     * - Fetches the assignments with the current search text, sorting order, and specified parameters.
     * - Fetches the total count of assignments for pagination purposes.
     */
    const refreshCount = async () => {
        setAssign([]);
        setPageNumber(0);
        setPage(1);
        await fetchAssignments(pageNumber, textSearch, false, null, orderBy, order, 0);
        await fetchAssignmentsCount();
    };

    /**
     * Handles changes to the assignment tab selection and updates state variables
     * accordingly based on the selected tab value. It resets assignments list,
     * sets pagination to the first page, and performs appropriate logic for fetching
     * assignments depending on the desired tab and filter.
     *
     * The function performs the following:
     * - Resets the assignments list and the page number.
     * - Modifies UI state variables such as the selected tab and flags for active tabs.
     * - Invokes the function to fetch assignments with updated parameters based on
     *   the user's selected tab value.
     *
     * Tab Options:
     * - "allAssignment": Fetches all assignments.
     * - "completed": Fetches assignments marked as completed.
     * - "readyForReview": Fetches assignments pending for review.
     * - Default (e.g., "myAssignment"): Fetches assignments specific to the user.
     *
     * @param {Object} event - The event object triggered by the user's action.
     * @param {Object} event.currentTarget - The target element that was acted upon.
     * @param {string} event.currentTarget.value - The value of the selected tab,
     * which determines the type of assignments to retrieve.
     */
    const handleChangeAssignment = (event) => {
        setAssign([]);
        setPageNumber(0);
        if (tabValue === 'myAssignment') {
            setValue(event.currentTarget.value);
            if (event.currentTarget.value === 'allAssignment') {
                reset();
                const myAssignment = false;
                const page = 0;
                setInMyAssignmentTab(false);
                setInReadyReviewTab(false);
                setPage(1);
                fetchAssignments(page, textSearch, myAssignment, null, orderBy, order, 0);
            } else if (event.currentTarget.value === 'completed') {
                reset();
                const myAssignment = false;
                setInMyAssignmentTab(false);
                setInReadyReviewTab(false);
                const tab = 'COMPLETED';
                setPage(1);
                fetchAssignments(pageNumber, textSearch, myAssignment, tab, orderBy, order, 0);
            } else if (event.currentTarget.value === 'readyForReview') {
                reset();
                const myAssignment = false;
                setInReadyReviewTab(true);
                setInMyAssignmentTab(false);
                const tab = 'PENDING_REVIEW';
                setPage(1);
                fetchAssignments(pageNumber, textSearch, myAssignment, tab, orderBy, order, 0);
            } else {
                reset();
                const myAssignment = true;
                setInMyAssignmentTab(true);
                setInReadyReviewTab(false);
                const tab = 'MY_ASSIGNMENT';
                setPage(1);
                fetchAssignments(pageNumber, textSearch, myAssignment, tab, orderBy, order, 0);
            }
        }
    };

    /**
     * Function to trigger the display of the "Add Assignment" interface or modal.
     * Sets the `showAddAssignment` state to `true`, indicating that the UI component
     * responsible for adding a new assignment should be displayed.
     */
    const addAssignment = () => {
        setshowAddAssignment(true);
    };

    /**
     * Closes the current assignment modal and resets related states.
     *
     * This function performs the following actions:
     * - Hides the add assignment modal by setting `setshowAddAssignment` to false.
     * - Clears the assignment list by resetting `setAssign` to an empty array.
     * - Resets the page number using `setPageNumber`.
     * - Updates the state and fetches assignments based on the tab (`value`) selected:
     *   - `'allAssignment'`: Clears the current tab filters, sets the pagination to the first page,
     *     and fetches all assignments.
     *   - `'myAssignment'`: Sets the appropriate tab state for "My Assignments",
     *     resets pagination, and fetches the user's assignments.
     *   - `'completed'`: Updates the state for the "Completed" tab,
     *     resets pagination, and fetches assignments marked completed.
     *   - `'readyForReview'`: Prepares the state for "Ready for Review" assignments,
     *     resets pagination, and fetches assignments pending review.
     * - Fetches the total count of assignments across tabs with `fetchAssignmentsCount`.
     */
    const handleClose = () => {
        setshowAddAssignment(false);
        setAssign([]);
        setPageNumber(0);
        if (value === 'allAssignment') {
            setInMyAssignmentTab(false);
            setInReadyReviewTab(false);
            setPage(1);
            fetchAssignments(pageNumber, textSearch, false, null, orderBy, order, 0);
        } else if (value === 'myAssignment') {
            setInMyAssignmentTab(true);
            setInReadyReviewTab(false);
            const tab = 'MY_ASSIGNMENT';
            setPage(1);
            fetchAssignments(pageNumber, textSearch, true, tab, orderBy, order, 0);
        } else if (value === 'completed') {
            setInMyAssignmentTab(false);
            setInReadyReviewTab(false);
            const tab = 'COMPLETED';
            setPage(1);
            fetchAssignments(pageNumber, textSearch, true, tab, orderBy, order, 0);
        } else if (value === 'readyForReview') {
            setInMyAssignmentTab(false);
            setInReadyReviewTab(true);
            const tab = 'PENDING_REVIEW';
            setPage(1);
            fetchAssignments(pageNumber, textSearch, true, tab, orderBy, order, 0);
        }
        fetchAssignmentsCount();
    };

    /**
     * Toggles the state of `finalResult` and updates other related states.
     *
     * This function is designed to handle click events by performing the following actions:
     * - If `finalResult` is true, it resets `finalResult` to false and clears the `scoreList`.
     * - If `finalResult` is false, it sets `finalResult` to true and clears the `scoreList`.
     * - Independently, if `highestLevel` is true, it resets `highestLevel` to false.
     */
    const handleClick = () => {
        if (finalResult) {
            setFinalResult(false);
            setScoreList([]);
        } else {
            setFinalResult(true);
            setScoreList([]);
        }
        if (highestLevel) {
            setHighestLevel(false);
        }
    };

    /**
     * Toggles the state of the highest level reached, resets the score list,
     * and conditionally resets the final result.
     *
     * This function performs the following actions:
     * - Toggles the current state of the "highest level" variable.
     * - Resets the score list to an empty array.
     * - If the final result is currently set, it resets it to false.
     */
    const handleHighestLevelReached = () => {
        setHighestLevel(!highestLevel);
        setScoreList([]);
        if (finalResult) {
            setFinalResult(false);
        }
    };

    /**
     * Handles user interaction for executing a search operation.
     * Triggers a search when the 'Enter' key is pressed on the keyboard
     * or when a mouse click event occurs.
     *
     * @param {Event} e - The event object containing details of the user interaction.
     *                     It can either be a keyboard or mouse event.
     */
    const search = (e) => {
        if (e.key === 'Enter' || (e.type === 'mousedown')) {
            setScoreList([]);
            setAssign([]);
            setPageNumber(0);
            setTextSearch(text);
        }
    };

    /**
     * An asynchronous function that handles retrieving and updating the score list.
     * It resets the page number and score list, determines the appropriate filter data,
     * sets the sorting page, and fetches the updated score list based on the specified parameters.
     *
     * The function ensures that initial conditions are met for displaying or processing the score list,
     * and dynamically adjusts the data to match the selected filters and sorting preferences.
     */
    const handleScore = async () => {
        const pageNo = 0;
        setPageNumber(0);
        setScoreList([]);
        let filterData;
        if (!filterDatas.product) {
            filterData = {
                ...filterDatas,
                product: getDefaultProduct(),
            };
        } else {
            filterData = filterDatas;
        }
        setSortPage(1);
        fetchScoreList(pageNo, textSearch, finalResult, highestLevel, filterData, sortBy, sort, 0);
    };

    /**
     * Handles the sorting logic for a given property.
     *
     * This function determines the new sort order (ascending or descending)
     * based on the current state of the sorting column and order. If the
     * current column is the same as the provided property and the sort order
     * is ascending, the order is switched to descending. Otherwise, the order
     * is set to ascending.
     *
     * @param {string} property - The column or property based on which sorting needs to be applied.
     * @returns {string} The updated sort order, either 'asc' for ascending or 'desc' for descending.
     */
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        return isAsc ? 'desc' : 'asc';
    };

    /**
     * Sorts and updates the data properties for assignments based on the specified property.
     *
     * @param {string} property - The property name to sort by (e.g., column or field name).
     *
     * Updates the state and fetches the appropriate assignments based on the current value
     * of the filter (`value`), which determines the type of assignments to fetch:
     *  - `allAssignment`: Fetches all assignments.
     *  - `myAssignment`: Fetches only assignments assigned to the current user.
     *  - `completed`: Fetches completed assignments.
     *  - `readyForReview`: Fetches assignments ready for review.
     *
     * For each filter type, it resets the page number, sets ordering configurations, and triggers
     * a data fetch.
     */
    const sortProperties = (property) => {
        const ord = handleRequestSort(property);
        const ordBy = property;
        setPageNumber(0);
        setOrderBy(property);
        setAssign([]);
        let tab;
        if (value === 'allAssignment') {
            setPage(1);
            fetchAssignments(0, textSearch, false, null, ordBy, ord, 0);
        } else if (value === 'myAssignment') {
            tab = 'MY_ASSIGNMENT';
            setPage(1);
            fetchAssignments(0, textSearch, true, tab, ordBy, ord, 0);
        } else if (value === 'completed') {
            tab = 'COMPLETED';
            setPage(1);
            fetchAssignments(0, textSearch, true, tab, ordBy, ord, 0);
        } else if (value === 'readyForReview') {
            tab = 'PENDING_REVIEW';
            setPage(1);
            fetchAssignments(0, textSearch, true, tab, ordBy, ord, 0);
        }
    };

    /**
     * Handles the sorting logic for a dataset based on a given property.
     *
     * @param {string} property - The property by which the data should be sorted.
     * @returns {string} The updated sorting order, either 'asc' for ascending or 'desc' for descending.
     */
    const handleSort = (property) => {
        const isAsc = sortBy === property && sort === 'asc';
        setSort(isAsc ? 'desc' : 'asc');
        return isAsc ? 'desc' : 'asc';
    };

    /**
     * Function to handle sorting of data based on a specific property.
     *
     * @function sorting
     * @param {string} property - The property by which the data should be sorted.
     * @description
     * This function triggers the sorting of data by:
     * - Determining the order (`asc` or `desc`) for the specified property using `handleSort`.
     * - Resetting the page number to 0 and clearing the current score list.
     * - Setting the sort configuration based on the provided property.
     * - Establishing or maintaining filter settings for the sorting operation.
     * If the product filter is not set, it initializes the filter using the first product ID from the list of products.
     * When the page number is 0, it sets the current page to 1 and fetches the score list data using the relevant filters and sort parameters.
     */
    const sorting = (property) => {
        const ord = handleSort(property);
        const ordBy = property;
        setPageNumber(0);
        setSortBy(property);
        setScoreList([]);
        let filterData;
        if (!filterDatas.product) {
            filterData = {
                ...filterDatas,
                product: getDefaultProduct(),
            };
        } else {
            filterData = filterDatas;
        }
        if (pageNumber === 0) {
            const pageNum = 0;
            setSortPage(1);
            fetchScoreList(pageNum, textSearch, finalResult, highestLevel, filterData, ordBy, ord, 0);
        }
    };

    /**
     * Downloads an Excel file containing assignment or score data based on the given parameters.
     *
     * This method determines the type of data to be downloaded (either assignments or score lists)
     * using a series of conditional checks and parameters such as `tabValue`, `value`, `filterDatas`,
     * and others. It supports downloading assignment data for various categories (e.g., "completed",
     * "ready for review") or score lists for specific filters. The downloaded file is saved in
     * `.xlsx` format.
     *
     * Assignment Data Logic:
     * - Determines the `myAssignment` flag and `tab` value based on `tabValue` and `value`.
     * - Fetches assignments using `fetchAllAssignments` with appropriate parameters for task and type.
     * - Saves the resultant file with dynamically determined filenames.
     *
     * Score List Logic:
     * - Ensures a default product filter is applied if none is selected.
     * - Fetches score lists using `fetchScoreLists` with specified filters.
     * - Saves the resultant file as `Score_List.xlsx`.
     *
     * Dependencies:
     * - `fetchAllAssignments`: A method used to retrieve assignment data based on filters.
     * - `fetchScoreLists`: A method used to retrieve score data based on specified filters.
     * - `FileSaver.saveAs`: A library utilized for saving the generated Excel file to the local system.
     * - `Blob`: Represents the data to be downloaded in the .xlsx format.
     * - `DashboardPermissions`: Provides task and type permissions for pending review assignments.
     *
     * Parameters (used in the method):
     * - `tabValue` (string): Indicates the current tab context (e.g., "myAssignment").
     * - `value` (string): Category of assignments to download (e.g., "allAssignment", "completed").
     * - `textSearch` (string): Search text used to filter the results.
     * - `filterDatas` (object): Object containing filter parameters such as `product`.
     * - `finalResult` (any): Additional filter criteria for score list fetching.
     * - `products` (array): A list of available products when filter information is missing.
     */
    const excelDownLoad = () => {
        let myAssignment;
        let tab;
        if (tabValue === 'myAssignment') {
            if (value === 'allAssignment') {
                myAssignment = false;
            } else if (value === 'completed') {
                myAssignment = false;
                tab = 'COMPLETED';
            } else if (value === 'readyForReview') {
                myAssignment = false;
                tab = 'PENDING_REVIEW';
            } else {
                myAssignment = true;
                tab = 'MY_ASSIGNMENT';
            }
            let task = null;
            let type = null;
            if (tab === 'MY_ASSIGNMENT' || tab === 'COMPLETED') {
                task = null;
                type = null;
            } else if (tab === 'PENDING_REVIEW') {
                task = DashboardPermissions.review_assignments.view.task;
                type = DashboardPermissions.review_assignments.view.type;
            }
            fetchAllAssignments(myAssignment, tab, textSearch, task, type).then(
                (response) => {
                    const fileName = value === 'allAssignment'
                        ? 'All_Assignment'
                        : value === 'myAssignment'
                            ? 'My_Assignment'
                            : value === 'completed'
                                ? 'Completed_Assignments'
                                : 'Ready_For_Review';
                    const blob = new Blob([response.data], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    FileSaver.saveAs(blob, `${fileName}.xlsx`);
                },
            );
        } else {
            if (!filterDatas.product) {
                filterDatas.product = getDefaultProduct();
            }
            const download = true;
            fetchScoreLists(textSearch, finalResult, download, filterDatas).then((response) => {
                const fileName = 'Score_List';
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                FileSaver.saveAs(blob, `${fileName}.xlsx`);
            });
        }
    };

    /**
     * Handles the selection of a date range by resetting the current page number
     * and enabling the date range selection mode. This function is typically used
     * to prepare the application for filtering or displaying data based on the
     * selected date range.
     */
    const dateSelection = () => {
        setPageNumber(0);
        setDateRange(true);
    };

    /**
     * Updates product-related data and fetches a new score list based on the provided filter parameters.
     *
     * This function resets the page number and score list, updates the filter data with specified
     * values, applies default values for `startDate` and `endDate` if not provided, and sets the
     * sort pagination. Finally, it fetches a new score list using the updated parameters.
     *
     * @function
     * @param {Object} filterData - The filter object containing parameters for querying product data.
     * The object may include properties such as `startDate` and `endDate`, along with any other relevant
     * filtering criteria.
     */
    const productChange = (filterData) => {
        setPageNumber(0);
        setScoreList([]);
        const pNum = 0;

        if (!filterData.product) {
            if (!defaultProduct) {
                setDefaultProduct(getDefaultProduct());
                setTimeout(() => productChange(filterData), 1000);
                return;
            }

            filterData.product = getDefaultProduct();
        }
        setFilterDatas(filterData);
        setSortPage(1);
        fetchScoreList(pNum, textSearch, finalResult, highestLevel, filterData, sortBy, sort, 0);
    };

    /**
     * Handles updates to a date range input field and updates the filter state accordingly.
     *
     * The function extracts the input value from the event target and updates
     * the `filterDatas` state object. If a product is already present in `filterDatas`,
     * it only updates the field corresponding to the input name with the new value.
     * If no product is present, it initializes a product field using the first product
     * from the `products` array before updating the input name field with the new value.
     *
     * @param {Object} evt - The event object triggered by the input change.
     * @param {Object} evt.target - The target element of the event.
     * @param {string} evt.target.name - The name attribute of the target input field.
     * @param {string} evt.target.value - The value of the target input field.
     */
    const handleDateRange = (evt) => {
        const { value } = evt.target;
        if (filterDatas.product) {
            setFilterDatas({
                ...filterDatas,
                [evt.target.name]: value,
            });
        } else {
            setFilterDatas({
                ...filterDatas,
                product: getDefaultProduct(),
                [evt.target.name]: value,
            });
        }
    };

    /**
     * Resets and updates the score list based on specified filters and sorting parameters.
     *
     * - Resets the score list to an empty state.
     * - Resets the sorting page to the first page.
     * - Fetches a new score list based on the provided parameters, including pagination, search text,
     *   result filtering, level filtering, additional filters, sorting criteria, sorting direction,
     *   and initial offset.
     * - Resets the date range selection to its default state.
     *
     * This function is typically used to apply a new set of filters or sorting configurations
     * and refresh the displayed data accordingly.
     */
    const apply = () => {
        setScoreList([]);
        setSortPage(1);
        fetchScoreList(pageNumber, textSearch, finalResult, highestLevel, filterDatas, sortBy, sort, 0);
        setDateRange(false);
    };

    /**
     * Updates the filter parameters based on the specified time range type and triggers data fetching.
     *
     * @param {string} type - The type of time range to filter data by. Possible values include:
     *   - 'today': Filters data for today's date.
     *   - 'yesterday': Filters data for yesterday's date.
     *   - 'last_7_days': Filters data for the last 7 days (including today).
     *   - 'last_week': Filters data for the previous week (Monday to Sunday).
     *   - 'month_to_date': Filters data from the start of the current month to today's date.
     *   - 'previous_month': Filters data for the entirety of the previous month.
     *   - Any other value defaults to filtering data from the start of the current year to today's date.
     *
     * The function modifies the `filterDatas` object in the following ways:
     *   1. If no product is selected in `filterDatas`, initializes it with the first product from the `products` array.
     *   2. Sets `startDate` and `endDate` in `filterDatas` based on the specified `type`.
     *
     * Additionally, the function performs the following operations:
     *   - Resets the date range selector state by calling `setDateRange(false)`.
     *   - Clears the score list by calling `setScoreList([])`.
     *   - Resets the page number of sorted data to 1 using `setSortPage(1)`.
     *   - Fetches updated filtered data by calling `fetchScoreList` with updated parameters including `filterDatas` and other filter/sort criteria.
     *
     * Dependencies:
     * - Uses the `moment` library for date manipulation.
     * - Relies on external variables: `filterDatas`, `products`, `setFilterDatas`, `setDateRange`, `setScoreList`, `setSortPage`, `fetchScoreList`, `pageNumber`, `textSearch`, `finalResult`, `highestLevel`, `sortBy`, and `sort`.
     */
    const filter = (type) => {
        if (!filterDatas.product) {
            setFilterDatas({
                ...filterDatas,
                product: getDefaultProduct(),
            });
            filterDatas.product = getDefaultProduct();
        }
        if (type === 'today') {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().format('YYYY-MM-DD');
            filterDatas.endDate = moment().format('YYYY-MM-DD');
        } else if (type === 'yesterday') {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().add(-1, 'days').format('YYYY-MM-DD'),
                endDate: moment().add(-1, 'days').format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().add(-1, 'days').format('YYYY-MM-DD');
            filterDatas.endDate = moment().add(-1, 'days').format('YYYY-MM-DD');
        } else if (type === 'last_7_days') {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().add(-6, 'days').format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().add(-7, 'days').format('YYYY-MM-DD');
            filterDatas.endDate = moment().format('YYYY-MM-DD');
        } else if (type === 'last_week') {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().day(-6).format('YYYY-MM-DD'),
                endDate: moment().day(0).format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().day(-6).format('YYYY-MM-DD');
            filterDatas.endDate = moment().day(0).format('YYYY-MM-DD');
        } else if (type === 'month_to_date') {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().startOf('month').format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().startOf('month').format('YYYY-MM-DD');
            filterDatas.endDate = moment().format('YYYY-MM-DD');
        } else if (type === 'previous_month') {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
                endDate: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
            filterDatas.endDate = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
        } else {
            setFilterDatas({
                ...filterDatas,
                startDate: moment().startOf('year').format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
            });
            filterDatas.startDate = moment().startOf('year').format('YYYY-MM-DD');
            filterDatas.endDate = moment().format('YYYY-MM-DD');
        }
        setDateRange(false);
        setScoreList([]);
        setSortPage(1);
        fetchScoreList(pageNumber, textSearch, finalResult, highestLevel, filterDatas, sortBy, sort, 0);
    };

    /**
     * Resets the date range and associated filters, and fetches a refreshed score list.
     *
     * This function performs the following actions:
     * 1. Disables the date range by setting the dateRange state to false.
     * 2. Clears the start and end dates in the filter data.
     * 3. Resets the score list to an empty array.
     * 4. Resets the current page to the first page.
     * 5. Fetches a new score list using the updated filter criteria and default sort/page settings.
     */
    const cancelDate = () => {
        setDateRange(false);
        setFilterDatas({
            ...filterDatas,
            startDate: '',
            endDate: '',
        });
        setScoreList([]);
        const filterData = filterDatas;
        filterData.startDate = '';
        filterData.endDate = '';
        setSortPage(1);
        fetchScoreList(pageNumber, textSearch, finalResult, highestLevel, filterData, sortBy, sort, 0);
    };

    /**
     * Handles the change of the current page in a paginated view.
     *
     * This function updates the current page and calculates the number of records to skip based on the new page.
     * Depending on the current tab (`value`), it fetches the corresponding assignments by invoking the `fetchAssignments` function.
     *
     * @param {Object} event - The event triggered by the page change.
     * @param {number} newPage - The new page number selected by the user.
     */
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        const skip = (newPage - 1) * 10;
        if (value === 'allAssignment') {
            const tab = 'ALL_ASSIGNMENT';
            fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, skip);
        } else if (value === 'myAssignment') {
            const tab = 'MY_ASSIGNMENT';
            fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, skip);
        } else if (value === 'completed') {
            const tab = 'COMPLETED';
            fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, skip);
        } else if (value === 'readyForReview') {
            const tab = 'PENDING_REVIEW';
            fetchAssignments(pageNumber, textSearch, false, tab, orderBy, order, skip);
        }
    };

    /**
     * Handles changes to the page sort and updates the current view accordingly.
     *
     * This function is triggered by a page change event. It updates the current page for sorting and calculates the
     * offset for pagination based on the new page number. If the active tab (`tabValue`) is set to 'score' and
     * the `products` array contains entries, it fetches a new list of scores based on the provided parameters.
     *
     * Parameters like `filterDatas`, `sortBy`, `sort`, and `skip` are passed to the `fetchScoreList`
     * function to retrieve the updated score data based on the current state.
     *
     * @param {Object} event - The event object associated with the page change.
     * @param {number} newPage - The new page number selected by the user.
     */
    const handleSortPageChange = (event, newPage) => {
        setSortPage(newPage);
        const skip = (newPage - 1) * 10;
        if (tabValue === 'score' && products.length) {
            let filterData;
            if (!filterDatas.product) {
                filterData = {
                    ...filterDatas,
                    product: getDefaultProduct(),
                };
            } else {
                filterData = filterDatas;
            }
            fetchScoreList(pageNumber, textSearch, finalResult, highestLevel, filterData, sortBy, sort, skip);
        }
    };

    return (
        <div>
            {localStorage.getItem('backToDashboard')
            && !viewPermission
            && !userPermission ? (
                <h1 style={{ textAlign: 'center' }}>No Assignments</h1>
            ) : (
                <Grid container spacing={2}>
                    {showAddAssignment && (
                        <AddAssignment open={showAddAssignment} handleClose={handleClose} />
                    )}
                    <Grid item className={classes.grid3}>
                        {tabValue === 'score'
                            ? <ScoreFilters productChange={productChange}
                                            defaultProduct={getDefaultProduct()}
                                            setDefaultProduct={setDefaultProduct}
                            />
                            : (
                                <UserData
                                    userData={userData}
                                    readyForReviewCount={pendingForReview}
                                    countData={countData}
                                />
                            )}

                    </Grid>
                    <Grid item className={classes.grid9}>
                        {!permissionCheck(
                            DashboardPermissions.all_assignments.view.task,
                            DashboardPermissions.all_assignments.view.type,
                        )
                        && !permissionCheck(
                            DashboardPermissions.review_assignments.view.task,
                            DashboardPermissions.review_assignments.view.type,
                        )
                        && !permissionCheck(
                            SimsPermissions.assignment.view.task,
                            SimsPermissions.assignment.view.type,
                        ) ? (
                            <h2 style={{ textAlign: 'center' }}>
                                No Permission For View Assignments, Please Contact Your
                                Administrative!
                            </h2>
                        ) : (
                            <Paper elevation={0} className={classes.paper}>
                                <Grid
                                    container
                                    direction="row"
                                    alignItems="flex-start"
                                >
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            className={classes.title}
                                            variant="h6"
                                            id="tableTitle"
                                            component="div"
                                        >
                                            {tabValue !== 'score' ? 'My Assignments' : 'My Scores'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <List
                                            className={classes.padding}
                                            style={{ float: 'right' }}
                                        >
                                            <ListItem>
                                                <div className={classes.search}>
                                                    <InputBase
                                                        value={text}
                                                        placeholder="Search Assignments"
                                                        className={classes.inputSearch}
                                                        inputProps={{ 'aria-label': 'search' }}
                                                        onChange={(e) => setText(e.target.value)}
                                                        onKeyDown={(e) => search(e)}
                                                    />
                                                    <div className={classes.searchBtn}>
                                                        <Button onMouseDown={(e) => search(e)}><SearchIcon /></Button>
                                                    </div>
                                                </div>
                                                {tabValue === 'score'
                                                    ? (
                                                        <div className={classes.search}>
                                                            <InputBase
                                                                value={filterDatas.startDate && filterDatas.endDate
                                                                    ? `${moment(filterDatas.startDate).format('DD-MM-YYYY')}-${moment(filterDatas.endDate).format('DD-MM-YYYY')}` : ''}
                                                                placeholder="Select Date Range"
                                                                className={classes.inputSearch}
                                                                name="dateRange"
                                                            />
                                                            <div className={classes.searchBtn}>
                                                                <Button onClick={dateSelection}><DateRangeIcon /></Button>
                                                            </div>
                                                        </div>
                                                    ) : ''}
                                                {tabValue !== 'score' && userDetails.role.toLowerCase() !== 'agent'
                                                    ? (
                                                        <div className={classes.assignmentDiv}>
                                                            <Tooltip
                                                                title={
                                                                    !permissionCheck(
                                                                        SimsPermissions.assignment.create.task,
                                                                        SimsPermissions.assignment.create.type,
                                                                    )
                                                                        ? 'you dont have permission to create'
                                                                        : ''
                                                                }
                                                                placement="bottom"
                                                            >
                                                                <span>
                                                                    <Button
                                                                        variant="contained"
                                                                        disableElevation
                                                                        className={classes.addButton}
                                                                        onClick={addAssignment}
                                          disabled={
                                              !permissionCheck(
                                                                                SimsPermissions.assignment.create.task,
                                                                                SimsPermissions.assignment.create.type,
                                              )
                                          }
                                                                    >
                                                                        <span className={classes.plus}>+</span>
                                                                        &nbsp;ADD ASSIGNMENT
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                            <Button
                                                                size="small"
                                                                disableElevation
                                                                className={classes.buttonSqr}
                                                                onClick={excelDownLoad}
                                                            >
                                                                <img
                                                                    className={classes.img}
                                                                    height={30}
                                                                    src={Export}
                                                                    alt="export"
                                                                />
                                                            </Button>
                                                        </div>
                                                    ) : null}
                                            </ListItem>
                                        </List>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    direction="row"
                                    alignItems="flex-start"
                                    className={classes.tabs}
                                >
                                    {tabValue === 'myAssignment' ? (
                                        <>
                                            {permissionCheck(
                                                DashboardPermissions.all_assignments.view.task,
                                                DashboardPermissions.all_assignments.view.type,
                                            ) ? (
                                                <Grid item xs={4} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        value="allAssignment"
                                                        onClick={handleChangeAssignment}
                                                        className={value === 'allAssignment' ? classes.active : classes.inActive}
                                                    >
                                                        All Assignment
                                                        ({countData.allAssignmentCount})
                                                    </Button>
                                                </Grid>
                                            ) : null}
                                            {permissionCheck(
                                                SimsPermissions.assignment.view.task,
                                                SimsPermissions.assignment.view.type,
                                            )
                                            || permissionCheck(
                                                SimsPermissions.assignment.create.task,
                                                SimsPermissions.assignment.create.type,
                                            )
                                            || permissionCheck(
                                                SimsPermissions.assignment.delete.task,
                                                SimsPermissions.assignment.delete.type,
                                            ) ? (
                                                <Grid item xs={4} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        value="myAssignment"
                                                        onClick={handleChangeAssignment}
                                                        className={value === 'myAssignment' ? classes.active : classes.inActive}
                                                    >
                                                        My Assignments

                                                        (
                                                        {countData.myAssignmentCount}
                                                        )
                                                    </Button>
                                                </Grid>
                                            ) : null}
                                            {permissionCheck(
                                                SimsPermissions.assignment.view.task,
                                                SimsPermissions.assignment.view.type,
                                            )
                                            || permissionCheck(
                                                SimsPermissions.assignment.create.task,
                                                SimsPermissions.assignment.create.type,
                                            )
                                            || permissionCheck(
                                                SimsPermissions.assignment.delete.task,
                                                SimsPermissions.assignment.delete.type,
                                            ) ? (
                                                <Grid item xs={4} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        value="completed"
                                                        onClick={handleChangeAssignment}
                                                        className={value === 'completed' ? classes.active : classes.inActive}
                                                    >
                                                        Completed

                                                        (
                                                        {countData.completedAssignmentCount}
                                                        )
                                                        {unopenedCompletedAssignments > 0
                                                            ? (
                                                                <Badge
                                                                    className={classes.badge}
                                                                    badgeContent={unopenedCompletedAssignments}
                                                                    max={999}
                                                                    showZero
                                                                    overlap={'circular'}
                                                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                                />
                                                            )
                                                            : ''
                                                        }
                                                    </Button>
                                                </Grid>
                                            ) : null}
                                            {/* {permissionCheck(
                                                DashboardPermissions.review_assignments.view.task,
                                                DashboardPermissions.review_assignments.view.type,
                                            ) && (user.role.toLowerCase() === 'agent') ? (
                                                <Grid item xs={4} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        value="readyForReview"
                                                        onClick={handleChangeAssignment}
                                                        className={value === 'readyForReview' ? classes.active : classes.inActive}
                                                    >
                                                        'Pending Review'  ({countData.readyForReviewCount})
                                                    </Button>
                                                </Grid>
                                            ) : null} */}
                                            {permissionCheck(
                                                DashboardPermissions.review_assignments.view
                                                    .task,
                                                DashboardPermissions.review_assignments.view
                                                    .type,
                                            ) ? (
                                                <Grid item xs={4} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        value="readyForReview"
                                                        onClick={handleChangeAssignment}
                                                        className={value === 'readyForReview' ? classes.active : classes.inActive}
                                                    >
                                                        {user.role.toLowerCase() === 'agent' ? 'Pending Review' : 'Ready For Review'}

                                                        (
                                                        {countData.readyForReviewCount}
                                                        )
                                                        {
                                                            (unopenedPendingAssignments > 0) &&
                                                            (user.role.toLowerCase() !== 'agent') ?
                                                                (
                                                                    <Badge
                                                                        className={classes.badge}
                                                                        badgeContent={unopenedPendingAssignments}
                                                                        overlap={'circular'}
                                                                        max={999}
                                                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                                        showZero
                                                                    />
                                                                ) : ''
                                                        }
                                                    </Button>
                                                </Grid>
                                            ) : null}
                                            {userDetails.role.toLowerCase() !== 'agent'
                                                ? (
                                                    <Grid item xs={4} sm={2}>
                                                        <Button
                                                            variant="contained"
                                                            value="score"
                                                            onClick={handleChange}
                                                            className={
                                                                tabValue === 'score' ? classes.active : classes.inActive
                                                            }
                                                        >
                                                            SCORE
                                                        </Button>
                                                    </Grid>
                                                ) : null}
                                        </>
                                    ) : null }
                                </Grid>
                                {tabValue === 'score'
                                    ? (
                                        <Grid
                                            container
                                            direction="row"
                                            alignItems="flex-start"
                                            className={classes.tabs}
                                        >
                                            <Grid item xs={4} sm={2}>
                                                <Button
                                                    variant="contained"
                                                    value="myAssignment"
                                                    onClick={handleChange}
                                                    className={
                                                        tabValue !== 'score' ? classes.active : classes.inActive
                                                    }
                                                >
                                                    Assignments
                                                </Button>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Button
                                                    variant="contained"
                                                    className={classes.excel}
                                                    onClick={excelDownLoad}
                                                >
                                                    Export Excel
                                                </Button>
                                            </Grid>
                                            <Grid item xs={2} sm={2} className={classes.checkLabel}>
                                                <FormControlLabel
                                                    className={classes.label}
                                                    control={(
                                                        <Checkbox
                                                            color="primary"
                                                            value={highestLevel}
                                                            checked={highestLevel}
                                                            name="final_result"
                                                            onChange={handleHighestLevelReached}
                                                        />
                                                    )}
                                                    label="Highest level reached"
                                                />
                                            </Grid>
                                            <Grid item xs={2} sm={2}>
                                                <FormControlLabel
                                                    className={classes.label}
                                                    control={(
                                                        <Checkbox
                                                            color="primary"
                                                            value={finalResult}
                                                            checked={finalResult}
                                                            name="final_result"
                                                            onChange={handleClick}
                                                        />
                                                    )}
                                                    label="Final result only"
                                                />
                                            </Grid>
                                        </Grid>
                                    ) : null}
                                <Grid container className={classes.container}>
                                    {dashboard_listing && assignments.length === 0 && tabValue !== 'score' ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    height: '70vh',
                                                    margin: 'auto',
                                                }}
                                            >
                                                <CircularProgress color="primary" />
                                            </div>
                                        )
                                        : tabValue === 'myAssignment'
                                        && (value === 'myAssignment'
                                            || 'allAssignment'
                                            || 'completed'
                                            || 'readyForReview') && (
                                            <Grid item xs={12} className={classes.gridWidth}>
                                                <AssignmentTable
                                                    assignments={assign}
                                                    inMyAssignmentTab={inMyAssignmentTab}
                                                    inReadyReviewTab={inReadyReviewTab}
                                                    assignment_loading={assignment_loading}
                                                    value={value}
                                                    sortProperties={sortProperties}
                                                    order={order}
                                                    orderBy={orderBy}
                                                    refreshCount={refreshCount}
                                                    total={
                                                        value === 'allAssignment'
                                                            ? allAssignmentCount
                                                            : value === 'myAssignment'
                                                                ? myAssignmentCount
                                                                : value === 'completed'
                                                                    ? completedAssignmentCount
                                                                    : readyForReviewCount
                                                    }
                                                    handlePageChange={handlePageChange}
                                                    addAssignment={addAssignment}
                                                    page={page}
                                                    allAssignmentCount={allAssignmentCount}
                                                />
                                            </Grid>
                                        )}
                                    {tabValue === 'score' && (
                                        <Grid item xs={12} className={classes.scoreTable}>
                                            <ScoreTable
                                                scoreList={scoreList}
                                                handleSortPageChange={handleSortPageChange}
                                                sortPage={sortPage}
                                                handleScore={handleScore}
                                                totalScore={totalScore}
                                                sorting={sorting}
                                                sort={sort}
                                                sortBy={sortBy}
                                                assignment_loading={assignment_loading}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            )}
            <DateFilter
                dateRange={dateRange}
                handleClose={handleClose}
                handleDateRange={handleDateRange}
                filterDatas={filterDatas}
                filter={filter}
                apply={apply}
                cancelDate={cancelDate}
            />
        </div>
    );
};

Dashboard.propTypes = {
    userData: PropTypes.object,
    fetchUserData: PropTypes.func,
    assignments: PropTypes.array,
    myAssignmentCount: PropTypes.number,
    allAssignmentCount: PropTypes.number,
    completedAssignmentCount: PropTypes.number,
    readyForReviewCount: PropTypes.number,
    fetchAssignments: PropTypes.func,
    fetchScoreList: PropTypes.func,
    scores: PropTypes.array,
    totalScore: PropTypes.number,
    pendingForReview: PropTypes.number,
    completedAssignCount: PropTypes.number,
    assignment_loading: PropTypes.bool,
    dashboard_listing: PropTypes.bool,
    permissions: PropTypes.array,
    userDetails: PropTypes.object,
    unopenedCompletedAssignments: PropTypes.number,
    unopenedPendingAssignments: PropTypes.number,
    simData: PropTypes.object,
    reset: PropTypes.func,
    products: PropTypes.array,
    fetchAssignmentsCount: PropTypes.func,
    countData: PropTypes.object,
};

/**
 * Maps Redux state to the Dashboard component props.
 *
 * @param {Object} state - The Redux store state.
 * @returns {Object} Props derived from state.
 */
const mapStateToProps = (state) => ({
    isLoading: state.sim.isLoading,
    totalSims: state.sim.total,
    assignments: state.sim.assignments ? state.sim.assignments.assignments : [],
    userData: state.sim.userData,
    simData: state.sim,
    allAssignmentCount: state.sim.assignments
        ? state.sim.assignments.allAssignmentCount
        : 0,
    myAssignmentCount: state.sim.assignments
        ? state.sim.assignments.myAssignmentCount
        : 0,
    completedAssignmentCount: state.sim.assignments
        ? state.sim.assignments.completedAssignmentCount
        : 0,
    readyForReviewCount: state.sim.assignments
        ? state.sim.assignments.readyForReviewCount
        : 0,
    pendingForReview: state.sim.assignments
        ? state.sim.assignments.pendingForReview
        : 0,
    completedAssignCount: state.sim.assignments
        ? state.sim.assignments.completedAssignCount
        : 0,
    unopenedCompletedAssignments: state.sim.assignments
        ? state.sim.assignments.unopenedCompletedAssignments
        : 0,
    unopenedPendingAssignments: state.sim.assignments
        ? state.sim.assignments.unopenedPendingAssignments
        : 0,
    scores: state.sim.scores ? state.sim.scores.scores : [],
    totalScore: state.sim.scores ? state.sim.scores.total : 0,
    assignment_loading: state.sim.assignment_loading,
    dashboard_listing: state.sim.dashboard_listing,
    permissions: state.login.permissions,
    userDetails: state.login.user,
    products: state.sim.products ? state.sim.products : [],
    countData: state.sim.assignmentDataCount,
});

/**
 * Maps Redux dispatch to the Dashboard component props.
 *
 * @type {Object}
 */
const mapDispatchToProps = {
    fetchAssignments,
    fetchUserData,
    fetchTotalAssignments,
    fetchScoreList,
    reset,
    fetchAssignmentsCount,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
